import { showError } from '@calendly/alert'
import { buildQuery, httpRequest } from '@calendly/http-tools'
import { I18n } from '@calendly/i18n'

import { redirectTo } from 'actions/core/navigation'
import { GROUP_ADMIN_ROLE } from 'constants/organization_roles'
import { fetchUserFeatures } from 'features/billing/actions/fetch_user_features'
import { showBillingMaintenanceBanner } from 'features/billing/billing_maintenance_banner_slice'
import { MEETING_INTELLIGENCE_ADDONS } from 'features/billing/modals/plan_selection_modal/config/addons'
import { steps } from 'features/billing/modals/subscription_modal/steps'
import {
  canManageBillingSelector,
  currentStageSelector,
  featureTierSelector,
  hasAddonsSelector,
  onlyHasMIAddonSelector,
  isDowngradeScheduledSelector,
  isManualSelector,
  isSubscriptionExpiringSelector,
  isSubscriptionPausedSelector,
  isSubscriptionCanceledSelector,
  isSubscriptionLockedSelector,
  paymentMethodSelector,
  unableToAddSeatsSelector,
  unableToChangeTierSelector,
  isCurrentSubscriptionLoadedSelector,
  onlyHasXPBOrMIAddonSelector,
  isSubscriptionPaymentPendingSelector
} from 'features/billing/selectors'
import { isQuickPurchaseSelector } from 'features/billing/selectors/quickPurchase'
import { orgInvitationPermissionsSelector } from 'selectors/current_organization'
import { isOnlyGroupAdminSelector } from 'selectors/current_user'

import {
  selectPlan as setNewCheckoutPlan,
  setAdditionalQuantity as setNewCheckoutAdditionalQuantity,
  setFeatureKey as setNewCheckoutFeatureKey,
  loadAvailableCurrencies,
  setSelectedAddons
} from '../checkout_info_slice'
import {
  setError,
  setTier,
  setIsCalendlyAllPlan,
  selectPlan,
  loadPlans,
  moveToStep,
  resetCreateSubscription,
  setInvitationsQuantity
} from '../create_subscription_slice'
import { fetchBillingPolicy } from './fetch_billing_policy'
import { fetchCurrentSubscription } from './fetch_current_subscription'
import { promptToRemoveScheduledDowngrade } from './prompt_to_remove_scheduled_downgrade'
import {
  filteredPlansSelector,
  dealSelector
} from '../modals/subscription_modal/selectors'


const selectDefaultPlan = () => async (dispatch, getState) => {
  const state = getState()
  const plans = filteredPlansSelector(state)
  const deal = dealSelector(state)
  if (deal) {
    dispatch(selectPlan(deal.plan.external_plan_id))
  } else {
    dispatch(selectPlan(plans[1].external_plan_id))
  }
}

export const moveToPayment = ({
  tier,
  onSuccess,
  featureKey,
  selectedAddons,
  analyticCheckoutFlowLocation
}) => async (dispatch, getState) => {
  const isSubscriptionLocked = isSubscriptionLockedSelector(getState())
  if (isSubscriptionLocked) {
    dispatch(showBillingMaintenanceBanner())
    return
  }

  const isCalendlyAllPlan = selectedAddons?.some(
    (addon) => MEETING_INTELLIGENCE_ADDONS.includes(addon)
  )

  dispatch(setIsCalendlyAllPlan(isCalendlyAllPlan))
  dispatch(setTier(tier))
  dispatch(setNewCheckoutPlan(tier))
  dispatch(setNewCheckoutFeatureKey(featureKey))
  await dispatch(selectDefaultPlan())

  if (selectedAddons) {
    dispatch(setSelectedAddons(selectedAddons))
  }

  dispatch(moveToStep({
    step: steps.PAYMENT,
    stepOptions: {
      onSuccess,
      featureKey,
      selectedAddons,
      analyticCheckoutFlowLocation
    }
  }))
}

export const fetchPlans = (data) => async (dispatch) => {
  const [success, response] = await httpRequest({
    url: '/api/billing/plans',
    method: 'GET',
    params: data,
    uniqueToken: 'billing_plans:fetch'
  })
  if (success) {
    dispatch(loadPlans(response))
    dispatch(loadAvailableCurrencies(response))
  } else if (response.type !== 'request_canceled') {
    dispatch(setError(response.message))
  }
}

export const moveToCheckout = (options = {}) => async (dispatch, getState) => {
  const {
    minQuantity,
    invitationsQuantity,
    onSuccess,
    isDowngrading,
    featureKey,
    preface,
    skipPreface,
    directToTier,
    analyticCheckoutFlowLocation,
    selectedAddons = null,
  } = options

  const state = getState()
  const hasAddons = hasAddonsSelector(state)
  const paymentMethod = paymentMethodSelector(state)
  const isCurrentSubscriptionLoaded = isCurrentSubscriptionLoadedSelector(state)
  const isPaused = isSubscriptionPausedSelector(state)
  const isSubscriptionCanceled = isSubscriptionCanceledSelector(state)
  const showAppleModal = paymentMethod === 'apple_store' && !isSubscriptionCanceled
  const onlyHasMIAddons = onlyHasMIAddonSelector(state)
  const onlyHasXPBOrMIAddon = onlyHasXPBOrMIAddonSelector(state)

  if (showAppleModal) {
    dispatch(moveToStep({
      step: steps.APPLE_SUBSCRIPTION_MODAL,
      stepOptions: {
        featureKey
      }
    }))
    return
  }

  if (isPaused) {
    dispatch(moveToStep({
      step: steps.SUBSCRIPTION_PAUSED
    }))
    return
  }

  if (hasAddons && !(onlyHasMIAddons || onlyHasXPBOrMIAddon)) {
    dispatch(moveToStep({
      step: steps.FEATURE_LOCKED_MODAL
    }))
    return
  }

  if (!isCurrentSubscriptionLoaded) {
    await dispatch(fetchCurrentSubscription())
  }

  const isSubscriptionLocked = isSubscriptionLockedSelector(state)
  if (isSubscriptionLocked) {
    dispatch(showBillingMaintenanceBanner())
    return
  }

  // always move to preface unless coming from preface, which should include skipPreface
  // preface will identify who gets old preface, new preface or moves on without preface
  if (!skipPreface) {
    dispatch(moveToStep({
      step: steps.PREFACE,
      stepOptions: options
    }))
    return
  }

  const [plansSuccess, billingPlans] = await httpRequest({
    url: '/api/billing/plans',
    uniqueToken: 'billing_plans:fetch:moveToCheckout',
    params: {
      quantity: minQuantity,
      invitations_quantity: invitationsQuantity
    }
  })

  if (!plansSuccess) {
    if (billingPlans.type !== 'request_canceled') {
      dispatch(showError(
        `${I18n.t('publisher.billing.failure.uh_oh')}. ${I18n.t('publisher.billing.failure.upgrade_problem_detected')}`
      ))
      dispatch(resetCreateSubscription())
    }
    return
  }

  dispatch(loadPlans(billingPlans))
  dispatch(loadAvailableCurrencies(billingPlans))
  if (billingPlans.deal) {
    const returnToPath = window.location.pathname + window.location.search
    const returnTo = buildQuery({ return_to: returnToPath })
    dispatch(redirectTo(`/payment/${billingPlans.deal.uuid}?${returnTo}`))
  } else if (directToTier) {
    dispatch(moveToPayment({ tier: directToTier, onSuccess, featureKey, selectedAddons, analyticCheckoutFlowLocation }))
  } else {
    dispatch(moveToStep({
      step: steps.PLAN_SELECTION,
      stepOptions: { onSuccess, preface, featureKey, isDowngrading, analyticCheckoutFlowLocation }
    }))
  }
}

export const completeChangePlan = () => async (dispatch) => {
  dispatch(resetCreateSubscription())
  await dispatch(fetchCurrentSubscription())
  await dispatch(fetchBillingPolicy())
  await dispatch(fetchUserFeatures())
}

export const moveToChangeSubscription = ({
  minQuantity,
  invitationsQuantity,
  onSuccess,
  preface,
  featureKey,
  paywallOverride,
  quotaAnalytics,
  refetchSub = true
}) => async (dispatch, getState) => {
  if (refetchSub) {
    await dispatch(fetchCurrentSubscription())
    await dispatch(fetchBillingPolicy())
  }

  const state = getState()

  // reroute to apple step for apple subscriptions
  // except for canceled subscriptions
  const paymentMethod = paymentMethodSelector(state)
  const canManageBilling = canManageBillingSelector(state)
  const isSubscriptionCanceled = isSubscriptionCanceledSelector(state)
  const showAppleModal = paymentMethod === 'apple_store' && !isSubscriptionCanceled && canManageBilling

  if (showAppleModal) {
    dispatch(moveToStep({
      step: steps.APPLE_SUBSCRIPTION_MODAL,
      stepOptions: {
        featureKey
      }
    }))
    return
  }

  const isPaused = isSubscriptionPausedSelector(state)
  const isPaymentPending = isSubscriptionPaymentPendingSelector(state)

  if (isPaused && canManageBilling) {
    dispatch(moveToStep({
      step: steps.SUBSCRIPTION_PAUSED
    }))
    return
  }

  if (isPaymentPending && canManageBilling) {
    dispatch(moveToStep({
      step: steps.SUBSCRIPTION_PAYMENT_PENDING
    }))
    return
  }

  const stage = currentStageSelector(state)
  const unableToAddSeats = unableToAddSeatsSelector(state)
  const unableToUpdateSubscription = isManualSelector(state)
  const attemptingToIncreaseQuantity = !!invitationsQuantity
  const minTier = featureTierSelector(state, { featureKey })
  const isDowngradeScheduled = isDowngradeScheduledSelector(state)
  const unableToChangeTier = unableToChangeTierSelector(state)
  const isSubscriptionExpiring = isSubscriptionExpiringSelector(state)
  const isQuickPurchase = isQuickPurchaseSelector(state)
  const checkForScheduledDowngrade = promptToRemoveScheduledDowngrade(dispatch, {
    isDowngradeScheduled,
    unableToChangeTier,
    isSubscriptionExpiring
  })

  const orgInvitationPermissions = orgInvitationPermissionsSelector(state)
  const isOnlyGroupAdmin = isOnlyGroupAdminSelector(state)

  const groupAdminsCanInviteUsers = isOnlyGroupAdmin && orgInvitationPermissions === GROUP_ADMIN_ROLE

  if (groupAdminsCanInviteUsers) {
    dispatch(moveToStep({
      step: steps.CONTACT_ADMIN_NO_SEATS,
      stepOptions: {
        onClose: () => dispatch(resetCreateSubscription())
      }
    }))
    return
  }

  if (invitationsQuantity) {
    dispatch(setInvitationsQuantity(invitationsQuantity))
    dispatch(setNewCheckoutAdditionalQuantity(invitationsQuantity))
  }

  // check if they should contact their admin
  if (!canManageBilling) {
    dispatch(moveToStep({
      step: steps.CONTACT_ADMIN,
      stepOptions: {
        onClose: async () => {
          dispatch(resetCreateSubscription())
        }
      }
    }))
    return
  }

  // contact support for manual customers
  if (unableToUpdateSubscription) {
    if (attemptingToIncreaseQuantity) {
      dispatch(moveToStep({
        step: steps.SUPPORT,
        stepOptions: {
          action: 'increase_subscription_quantity',
          data: { min_quantity: minQuantity },
          onClose: () => dispatch(resetCreateSubscription())
        }
      }))
    } else {
      dispatch(moveToStep({
        step: steps.SUPPORT,
        stepOptions: {
          action: 'upgrade_subscription_tier',
          data: { min_tier: minTier },
          onClose: () => dispatch(resetCreateSubscription())
        }
      }))
    }
    return
  }

  // block change if scheduled downgrade present
  const scheduledDowngradePresent = checkForScheduledDowngrade({})
  if (scheduledDowngradePresent) return

  // increase quantity
  if (stage === 'paid' && attemptingToIncreaseQuantity && !unableToAddSeats) {
    dispatch(moveToStep({
      step: steps.INCREASE_QUANTITY,
      stepOptions: {
        onClose: () => dispatch(resetCreateSubscription()),
        onSuccess
      }
    }))
    return
  }

  if (isQuickPurchase) {
    dispatch(moveToStep({
      step: steps.QUICK_PURCHASE,
      stepOptions: {
        onClose: () => dispatch(resetCreateSubscription())
      }
    }))
    return
  }

  // upgrade
  const checkoutOptions = {
    minQuantity,
    invitationsQuantity,
    preface,
    featureKey,
    onSuccess,
    paywallOverride,
    quotaAnalytics
  }

  dispatch(moveToCheckout(checkoutOptions))
}
